import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MultiSelect, type DropdownOption, BulkEditSectionFormService } from '@zag/ui';
import type { ManagedUser } from '@zag/auth/models';

const SECTION_ID = 'bulk-members';

interface State {
  userIds: string[];
}

@Component({
  selector: 'sc-members-bulk-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MultiSelect, TranslatePipe],
  template: `
    <zag-multi-select
      [options]="userOptions()"
      [placeholder]="'bulkEdit.members.selectUsers' | translate"
      [searchable]="true"
      [searchPlaceholder]="'bulkEdit.members.searchUsers' | translate"
      [noResultsText]="'bulkEdit.members.noUsers' | translate"
      [clearAriaLabel]="'bulkEdit.members.clearUsers' | translate"
      [removeAriaLabel]="'bulkEdit.members.removeUser' | translate"
      [ngModel]="state().userIds"
      (ngModelChange)="onUserIdsChange($event)"
    />
  `,
})
export class MembersBulkSectionComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly formService = inject(BulkEditSectionFormService);

  protected readonly state = signal<State>({ userIds: [] });

  private readonly usersResource = rxResource({
    stream: () => this.http.get<ManagedUser[]>('/api/v1/rbac/users'),
  });

  protected readonly userOptions = computed<DropdownOption<string>[]>(() =>
    (this.usersResource.value() ?? []).map(user => ({
      value: user.id,
      label: `${user.name} (${user.email})`,
    }))
  );

  protected onUserIdsChange(userIds: string[]): void {
    this.state.set({ userIds: [...userIds] });
  }

  ngOnInit(): void {
    this.formService.register(SECTION_ID, () => this.state());
  }

  ngOnDestroy(): void {
    this.formService.unregister(SECTION_ID);
  }
}
